import { eventConfig } from '../../../../config/event';
import { useFlag } from '../../../../providers/flags-provider';
import { AgendaSideButtonDisplayOptions, defaultAgendaDisplayOptions } from '../agenda';
import { getDocumentDownload, getVideo, getVideoStartTime } from '../util/agenda-transformer';
import { ProgramEvent } from '@cue/api';
import { Button, Tooltip } from '@cue/atoms';
import { useIcs, useMailto } from '@cue/hooks';
import { VideoTypes } from '@cue/molecules';
import { Modal, useModal } from '@cue/organisms';
import { StyledComponent } from '@cue/theme';
import { AnalyticsTransformer, TimeUtil } from '@cue/utility';
import { CSSObject } from '@emotion/react';
import styled from '@emotion/styled';
import { PlayerType } from '@project/hooks/use-media-analytics';
import { useResendAccess } from '@project/hooks/use-resend-access';
import { useUser } from '@project/hooks/use-user';
import { useUserSubscriptions } from '@project/hooks/use-user-subscriptions';
import { Flag } from '@project/providers/flags-provider/types';
import { Vod } from '@project/specific/components/vod';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

export const AgendaEventCta: React.FC<
  AgendaEventCtaProps & StyledComponent<AgendaEventCtaCSSOverride>
> = (props) => {
  const {
    id,
    start,
    end,
    title,
    includeCta = [
      AgendaEventCtaTypes.BOOKMARK,
      AgendaEventCtaTypes.ICS,
      AgendaEventCtaTypes.MAIL,
      AgendaEventCtaTypes.DOCUMENTDOWNLOAD,
      AgendaEventCtaTypes.SHARING,
    ],
    isVodVisibleFlag,
    links,
    agendaSideButtonDisplayOptions,
    disabled,
  } = props;

  const displayOptions = {
    ...defaultAgendaDisplayOptions.sideButtons,
    ...agendaSideButtonDisplayOptions,
  };
  const { setIsOpen } = useResendAccess();
  const { t } = useTranslation();
  const { isAuthenticated } = useUser();
  const { bookmarks, toggleBookmark } = useUserSubscriptions();
  const isBookmarked = bookmarks.data?.includes(id);
  const isFutureEvent = TimeUtil.getNowUtc().isBefore(TimeUtil.getUtc(end));
  const _vodVisibleFlag = useFlag(String(isVodVisibleFlag));
  const vodVisible = isVodVisibleFlag && _vodVisibleFlag;
  const videoStartTime = getVideoStartTime(links ?? []);
  const videoUrl = getVideo(links ?? []);
  const documentUrl = getDocumentDownload(links ?? []);
  const [modalRef] = useModal();
  const shareLink = `${location.origin}/share-event/${id}/?path=${encodeURIComponent(
    location.pathname.replace(/\/(..)\/my-event/, '/$1').replace(/\/(..)\/onsite-mode/, '/$1'),
  )}`;

  const ctaData = {
    title,
    url: shareLink,
    regUrl: 'regUrl',
    date: TimeUtil.getUtc(start).format('LL'),
    time: `${TimeUtil.convertToTimeZone(TimeUtil.getUtc(start), eventConfig.timezone).format(
      'HH:mm',
    )}`,
  };

  const [shareCopied, setShareCopied] = useState(false);
  const { download: downloadIcs } = useIcs();
  const { send: sendMail } = useMailto();

  const toolTipFavId = useRef(`bookmark-tooltip${uuidv4()}`);
  const toolTipMailId = useRef(`mail-tooltip${uuidv4()}`);
  const toolTipSharingId = useRef(`sharing-tooltip${uuidv4()}`);
  const toolTipCalId = useRef(`cal-tooltip${uuidv4()}`);
  const toolTipVod = useRef(`download-tooltip${uuidv4()}`);
  const toolTipDownload = useRef(`download-tooltip${uuidv4()}`);

  return (
    <Container className="cue-agenda-event-cta-wrapper">
      {vodVisible !== false && videoUrl && displayOptions?.play && (
        <>
          <Button
            data-tooltip-id={toolTipVod.current}
            icon="play"
            data-test-id="cue-test-agendacta-play"
            styling="agendaCtaRed"
            className="cue-agenda-event-cta-play-video"
            {...(disabled
              ? {}
              : {
                  onClick: () => {
                    modalRef.current?.open();
                    AnalyticsTransformer.trackEvent({
                      category: 'agenda',
                      type: 'share-ptn',
                      action: 'vod',
                      data: { uid: id },
                    });
                  },
                })}
          />

          <Tooltip place="bottom" variant="light" id={toolTipVod.current}>
            <strong>{t('agenda.tooltip.vod')}</strong>
          </Tooltip>
        </>
      )}
      {includeCta.includes(AgendaEventCtaTypes.BOOKMARK) && displayOptions?.bookmark && (
        <>
          <Button
            data-tooltip-id={toolTipFavId.current}
            icon={isBookmarked ? 'favouriteMarked' : 'favourite'}
            styling="agendaCta"
            data-test-id="cue-test-agendacta-bookmark"
            className={`cue-agenda-event-cta-bookmark`}
            {...(disabled
              ? {}
              : {
                  onClick: () => {
                    if (!isAuthenticated) {
                      setIsOpen({ isAdhoc: false });
                    } else {
                      toggleBookmark(id);

                      AnalyticsTransformer.trackEvent({
                        category: 'agenda',
                        type: 'bookmark',
                        action: !isBookmarked ? 'add' : 'remove',
                        data: { uid: id },
                      });
                    }
                  },
                })}
          />
          <Tooltip place="bottom" variant="light" id={toolTipFavId.current}>
            <strong>{t(`agenda.tooltip.bookmark${isBookmarked ? 'Remove' : ''}`)}</strong>
          </Tooltip>
        </>
      )}
      {includeCta.includes(AgendaEventCtaTypes.MAIL) && displayOptions?.shareViaMail && (
        <>
          <Button
            data-tooltip-id={toolTipMailId.current}
            icon="mailto"
            data-test-id="cue-test-agendacta-mailto"
            styling="agendaCta"
            className="cue-agenda-event-cta-mailto"
            {...(disabled
              ? {}
              : {
                  onClick: () => {
                    sendMail(
                      {
                        subject: t('agenda.mail-to.subject'),
                        body: t('agenda.mail-to.message'),
                      },
                      ctaData,
                    );

                    AnalyticsTransformer.trackEvent({
                      category: 'agenda',
                      type: 'share-ptn',
                      action: 'mail',
                      data: { uid: id },
                    });
                  },
                })}
          />
          <Tooltip place="bottom" variant="light" id={toolTipMailId.current}>
            <strong>{t('agenda.tooltip.mail-share')}</strong>
          </Tooltip>
        </>
      )}
      {includeCta.includes(AgendaEventCtaTypes.SHARING) && displayOptions?.sharing && (
        <>
          <Button
            data-tooltip-id={toolTipSharingId.current}
            icon="share"
            styling="agendaCta"
            data-test-id="cue-test-agendacta-sharing"
            className="cue-agenda-event-cta-sharing"
            {...(disabled
              ? {}
              : {
                  onClick: () => {
                    navigator.clipboard.writeText(shareLink);
                    setShareCopied(true);

                    AnalyticsTransformer.trackEvent({
                      category: 'agenda',
                      type: 'share-ptn',
                      action: 'sharing',
                      data: { uid: id },
                    });
                  },
                  onMouseOut: () =>
                    setTimeout(() => {
                      setShareCopied(false);
                    }, 200),
                })}
          />
          <Tooltip place="bottom" variant="light" id={toolTipSharingId.current}>
            <strong>
              {shareCopied ? t('agenda.tooltip.sharing-copied') : t('agenda.tooltip.sharing')}
            </strong>
          </Tooltip>
        </>
      )}
      {includeCta.includes(AgendaEventCtaTypes.ICS) && isFutureEvent && displayOptions?.iCal && (
        <>
          <Button
            data-tooltip-id={toolTipCalId.current}
            icon="ics"
            data-test-id="cue-test-agendacta-ics"
            styling="agendaCta"
            className="cue-agenda-event-cta-ics"
            {...(disabled
              ? {}
              : {
                  onClick: () => {
                    downloadIcs(
                      {
                        subject: t('agenda.ics.subject'),
                        start: TimeUtil.getUtc(start),
                        end: TimeUtil.getUtc(end),
                        description: t('agenda.ics.message') || '',
                        alarm: 30,
                      },
                      ctaData,
                    );

                    AnalyticsTransformer.trackEvent({
                      category: 'agenda',
                      type: 'share-ptn',
                      action: 'ics',
                      data: { uid: id },
                    });
                  },
                })}
          />
          <Tooltip place="bottom" variant="light" id={toolTipCalId.current}>
            <strong>{t('agenda.tooltip.ical')}</strong>
          </Tooltip>
        </>
      )}
      {includeCta.includes(AgendaEventCtaTypes.DOCUMENTDOWNLOAD) &&
        displayOptions.documentDownload &&
        documentUrl && (
          <>
            <Button
              data-tooltip-id={toolTipDownload.current}
              data-test-id="cue-test-agendacta-share-download"
              icon="download"
              styling="agendaCta"
              className="cue-agenda-event-cta-download"
              {...(disabled
                ? {}
                : {
                    onClick: () => {
                      window.open(documentUrl, '_blank');
                      AnalyticsTransformer.trackEvent({
                        category: 'agenda',
                        type: 'share-ptn',
                        action: 'download',
                        data: { url: documentUrl || '', uid: id },
                      });
                    },
                  })}
            />
            <Tooltip place="bottom" variant="light" id={toolTipDownload.current}>
              <strong>{t('agenda.tooltip.documentdownload')}</strong>
            </Tooltip>
          </>
        )}
      <Modal
        ref={modalRef}
        overrideCSS={{
          '.cue-modal-content': {
            width: '90vw',
            maxWidth: 1440,
          },
          '.cue-modal-close-button-wrapper': { right: '30px', top: '30px' },
        }}>
        {/*  <Video
          url={videoUrl}
          startTime={videoStartTime || 0}
          useMediaAnalytics={useMediaAnalytics}
          trackingTitle={JSON.stringify({ event: id })}
          videoType={VideoTypes.VoD}
          trackingType={PlayerType.VoD}
        /> */}

        <Vod
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          slug="Agenda"
          eventId={id}
          day={TimeUtil.getUtc(start)}
          url={videoUrl}
          startTime={videoStartTime || 0}
          videoType={VideoTypes.VoD}
          trackingType={PlayerType.VoD}
        />
      </Modal>
    </Container>
  );
};

export type AgendaEventCtaCSSOverride = {
  '.cue-agenda-event-cta-wrapper'?: CSSObject;
  '.cue-agenda-event-cta-bookmark'?: CSSObject;
  '.cue-agenda-event-cta-ics'?: CSSObject;
  '.cue-agenda-event-cta-mailto'?: CSSObject;
  '.cue-agenda-event-cta-download'?: CSSObject;
};
export interface AgendaEventCtaProps
  extends Pick<ProgramEvent, 'id' | 'start' | 'end' | 'title' | 'links'> {
  includeCta?: AgendaEventCtaTypes[];
  isVodVisibleFlag?: Pick<Flag, 'slug'>;
  agendaSideButtonDisplayOptions?: AgendaSideButtonDisplayOptions;
  disabled?: boolean;
}
export enum AgendaEventCtaTypes {
  BOOKMARK = 'BOOKMARK',
  ICS = 'ICS',
  MAIL = 'MAIL',
  DOCUMENTDOWNLOAD = 'DOCUMENTDOWNLOAD',
  SHARING = 'SHARING',
}

const Container = styled.div``;
